/**
 * AI Trial Room — Public Shop Leaderboard
 * Viral competition: Top-N shops by try-on count · Day / Week / Month tabs.
 * Everyone sees top performers → drives usage + word-of-mouth.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Trophy, Store, Crown, Award, Medal, RefreshCw } from 'lucide-react';
import api from '../../utils/api';

const PERIODS = [
  { id: 'day',   label: 'Today',   short: '📅 Day'    },
  { id: 'week',  label: 'This Week', short: '📆 Week',  hot: true },
  { id: 'month', label: 'Month',   short: '🗓️ Month'  },
  { id: 'all',   label: 'All Time', short: '🏛️ All'   },
];

const RANK_META = [
  { icon: Crown, label: 'Top Trial-Room Shop of Week', gradient: 'from-amber-400 via-yellow-400 to-orange-500', ring: 'ring-amber-300', emoji: '🥇' },
  { icon: Award, label: '2nd Place',                    gradient: 'from-slate-300 via-slate-400 to-slate-500',   ring: 'ring-slate-300', emoji: '🥈' },
  { icon: Medal, label: '3rd Place',                    gradient: 'from-orange-400 via-amber-500 to-yellow-600',   ring: 'ring-orange-300', emoji: '🥉' },
];

export default function TrialRoomLeaderboard() {
  const [period, setPeriod] = useState('week');
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const r = await api.get(`/trial-room/leaderboard?period=${p}&limit=10`);
      setLeaders(r.data?.leaders || []);
    } catch (_) { setLeaders([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  return (
    <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/60 via-slate-950/80 to-orange-950/60 p-4 space-y-3 relative overflow-hidden" data-testid="trial-leaderboard">
      {/* Sparkle bg */}
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(251,191,36,0.15) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(217,70,239,0.12) 0, transparent 40%)' }} />

      <div className="relative flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black flex items-center gap-1">
            <Trophy className="w-3 h-3" /> ॥ LIVE COMPETITION ॥
          </div>
          <h3 className="text-xl md:text-2xl font-black bg-gradient-to-r from-amber-300 via-orange-300 to-yellow-300 bg-clip-text text-transparent">
            🏆 Shop-Wise Public Leaderboard
          </h3>
          <div className="text-[11px] text-slate-300 mt-0.5">Top 10 shops by AI Trial-Room usage — <b className="text-amber-200">Aapka naam yahan aa sakta hai!</b></div>
        </div>
        <button
          onClick={() => load(period)}
          disabled={loading}
          data-testid="leaderboard-refresh"
          className="text-[11px] px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-slate-200 flex items-center gap-1 disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Period tabs */}
      <div className="relative flex gap-1 bg-black/40 rounded-lg p-1 border border-white/10 w-fit">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            data-testid={`leaderboard-period-${p.id}`}
            className={`px-2.5 py-1 rounded-md text-[11px] font-black transition ${period === p.id ? 'bg-amber-500 text-black' : 'text-slate-300 hover:bg-white/5'}`}>
            {p.short}{p.hot && period !== p.id && <span className="ml-0.5 text-[8px] text-amber-300">🔥</span>}
          </button>
        ))}
      </div>

      {/* Leaders */}
      {loading ? (
        <div className="relative text-center py-8 text-slate-400 text-xs">Loading leaderboard…</div>
      ) : leaders.length === 0 ? (
        <div className="relative text-center py-8 border-2 border-dashed border-white/10 rounded-xl">
          <Store className="w-10 h-10 mx-auto text-slate-500 mb-2 opacity-60" />
          <div className="text-slate-300 text-sm font-bold">Koi shop tracked nahi is period me.</div>
          <div className="text-[10px] text-slate-500 mt-1">🎯 Pehla try-on karke aap #1 ban sakte hain!</div>
        </div>
      ) : (
        <div className="relative space-y-2">
          {/* Top 3 podium */}
          {leaders.length >= 1 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
              {leaders.slice(0, 3).map((s, i) => {
                const meta = RANK_META[i];
                const Icon = meta.icon;
                return (
                  <div key={s.user_id} data-testid={`leaderboard-podium-${i + 1}`}
                    className={`rounded-xl p-3 border-2 bg-gradient-to-br ${meta.gradient} ring-2 ${meta.ring} relative overflow-hidden group`}>
                    <div className="absolute top-1 right-1 text-2xl opacity-90 drop-shadow">{meta.emoji}</div>
                    <div className="flex items-center gap-2 relative z-10">
                      {s.shop_logo_url ? (
                        <img src={s.shop_logo_url} alt={s.shop_name} className="w-10 h-10 rounded-full border-2 border-white/80 object-cover shadow-lg" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-black/40 border-2 border-white/80 flex items-center justify-center"><Store className="w-4 h-4 text-white" /></div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-[9px] uppercase tracking-widest text-black/80 font-black">#{i + 1} · {meta.label.split(' ')[0]}</div>
                        <div className="text-sm font-black text-white truncate" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>{s.shop_name}</div>
                        <div className="text-[10px] text-white/90 font-mono">{s.trials} try-ons</div>
                      </div>
                    </div>
                    {i === 0 && <div className="text-[9px] text-black/70 mt-1.5 font-black uppercase tracking-widest text-center bg-black/10 rounded py-0.5">
                      <Icon className="w-3 h-3 inline mr-0.5" /> Top Trial-Room Shop
                    </div>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Rest of the list (#4-10) */}
          {leaders.length > 3 && (
            <div className="rounded-xl border border-white/10 bg-black/40 divide-y divide-white/5">
              {leaders.slice(3).map((s, i) => (
                <div key={s.user_id} data-testid={`leaderboard-row-${i + 4}`} className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition">
                  <div className="w-8 text-center text-[11px] font-black text-amber-300 font-mono">#{i + 4}</div>
                  {s.shop_logo_url ? (
                    <img src={s.shop_logo_url} alt={s.shop_name} className="w-8 h-8 rounded-full object-cover border border-white/10" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><Store className="w-3.5 h-3.5 text-slate-400" /></div>
                  )}
                  <div className="flex-1 text-sm font-bold text-white truncate">{s.shop_name}</div>
                  <div className="text-xs font-mono text-cyan-300 font-black">{s.trials}<span className="text-[9px] text-slate-500 ml-0.5">try-ons</span></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="relative text-[9px] text-slate-500 text-center italic pt-1">
        💡 Rank badhane ke liye 2-Camera flow me try-ons badhao · Har week rankings reset hote hain
      </div>
    </div>
  );
}
